/**
 * LargeDocumentOptimizer - Performance optimization for 500+ page legal documents
 * 
 * Provides advanced optimization techniques for handling large legal documents
 * with smooth editing performance, memory efficiency, and responsive UI.
 */

import { LocalDocumentState, Operation } from '../types/DocumentTypes';
import { BrowserAPI, TypedOperation, OperationTypeGuards, TypedInsertOperation, TypedDeleteOperation } from '../types/BrowserTypes';
import { ViewportRenderer } from './ViewportRenderer';
import { MemoryManager } from './MemoryManager';
import { PerformanceMonitor } from './PerformanceMonitor';

interface OptimizationConfig {
  maxVisiblePages: number;        // Pages to render at once
  preloadPages: number;          // Pages to preload ahead/behind
  chunkSize: number;             // Text chunk size for processing
  memoryThreshold: number;       // Memory usage threshold (MB)
  performanceTarget: number;     // Target frame rate (fps)
  enableVirtualization: boolean; // Enable virtual scrolling
  enableLazyLoading: boolean;    // Enable lazy content loading
  enableBackgroundProcessing: boolean; // Background layout calculation
}

interface DocumentChunk {
  chunkId: string;
  startPosition: number;
  endPosition: number;
  content: string;
  isLoaded: boolean;
  isVisible: boolean;
  lastAccessed: Date;
  renderData?: ChunkRenderData;
}

interface ChunkRenderData {
  lines: TextLine[];
  height: number;
  width: number;
  glyphCache: Map<string, GlyphData>;
  layoutComplete: boolean;
}

interface TextLine {
  lineId: string;
  text: string;
  yPosition: number;
  height: number;
  glyphs: GlyphData[];
  formatting: LineFormatting;
}

interface GlyphData {
  character: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontFamily: string;
  fontSize: number;
  color: string;
}

interface LineFormatting {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string;
  color: string;
  backgroundColor?: string;
  textAlign: string;
  lineHeight: number;
}

interface OptimizationMetrics {
  totalChunks: number;
  loadedChunks: number;
  visibleChunks: number;
  memoryUsage: number;           // MB
  renderTime: number;            // ms
  frameRate: number;             // fps
  scrollPerformance: number;     // ms per scroll
  chunkLoadTime: number;         // ms
  layoutCalculationTime: number; // ms
}

export class LargeDocumentOptimizer {
  private viewportRenderer: ViewportRenderer;
  private memoryManager: MemoryManager;
  private performanceMonitor: PerformanceMonitor;
  
  private config: OptimizationConfig;
  private documentChunks: Map<string, DocumentChunk> = new Map();
  private visibleChunks: Set<string> = new Set();
  private loadedChunks: Set<string> = new Set();
  
  // Optimization state
  private _isOptimizing: boolean = false;
  private optimizationQueue: Operation[] = [];
  private _chunkingComplete: boolean = false;
  private _lastOptimization: Date = new Date();
  
  // Performance tracking
  private metrics: OptimizationMetrics;
  private performanceHistory: OptimizationMetrics[] = [];
  private lastHighMemWarnAt: number = 0;
  private lastLowFpsWarnAt: number = 0;
  private prevMemoryUsage: number = 0;
  
  // Background processing
  private layoutWorker: Worker | null = null;
  private chunkWorker: Worker | null = null;
  private debugEnabled: boolean = false;

  constructor(config: Partial<OptimizationConfig> = {}) {
    this.config = {
      maxVisiblePages: 3,
      preloadPages: 1,
      chunkSize: 10000,           // 10KB chunks
      memoryThreshold: 100,       // 100MB threshold
      performanceTarget: 60,      // 60fps target
      enableVirtualization: true,
      enableLazyLoading: true,
      enableBackgroundProcessing: true,
      ...config
    };
    
    this.metrics = {
      totalChunks: 0,
      loadedChunks: 0,
      visibleChunks: 0,
      memoryUsage: 0,
      renderTime: 0,
      frameRate: 60,
      scrollPerformance: 0,
      chunkLoadTime: 0,
      layoutCalculationTime: 0
    };
    
    this.viewportRenderer = new ViewportRenderer(this.config);
    this.memoryManager = new MemoryManager(this.config.memoryThreshold);
    this.performanceMonitor = new PerformanceMonitor();
    
    this.initializeBackgroundProcessing();
  }

  // ============================================================================
  // DOCUMENT CHUNKING
  // ============================================================================

  /**
   * Optimize document by chunking into manageable pieces
   */
  async optimizeDocument(documentState: LocalDocumentState): Promise<{
    success: boolean;
    chunksCreated: number;
    optimizationTime: number;
    memoryReduction: number;
  }> {
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Optimizing large document...');
    
    const startTime = performance.now();
    const initialMemory = this.memoryManager.getCurrentUsage();
    
    this._isOptimizing = true;
    
    try {
      // Clear existing chunks
      this.clearChunks();
      
      // Create document chunks
      const chunks = await this.createDocumentChunks(documentState);
      
      // Initialize viewport rendering
      await this.initializeViewportRendering(chunks);
      
      // Start background processing
      if (this.config.enableBackgroundProcessing) {
        this.startBackgroundProcessing(chunks);
      }
      
      const optimizationTime = performance.now() - startTime;
      const finalMemory = this.memoryManager.getCurrentUsage();
      const memoryReduction = initialMemory - finalMemory;
      
      this._chunkingComplete = true;
      this._lastOptimization = new Date();
      
      // Update metrics
      this.updateOptimizationMetrics();
      
      if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Optimization complete: ${chunks.length} chunks, ${optimizationTime.toFixed(1)}ms, ${memoryReduction.toFixed(1)}MB saved`);
      
      return {
        success: true,
        chunksCreated: chunks.length,
        optimizationTime,
        memoryReduction
      };
      
    } catch (error) {
      console.error('[LargeDocumentOptimizer] Optimization failed:', error);
      return {
        success: false,
        chunksCreated: 0,
        optimizationTime: performance.now() - startTime,
        memoryReduction: 0
      };
    } finally {
      this._isOptimizing = false;
    }
  }

  /**
   * Create document chunks for efficient processing
   */
  private async createDocumentChunks(documentState: LocalDocumentState): Promise<DocumentChunk[]> {
    const text = documentState.text;
    const chunks: DocumentChunk[] = [];
    
    let position = 0;
    let chunkIndex = 0;
    
    while (position < text.length) {
      const chunkEnd = Math.min(position + this.config.chunkSize, text.length);
      
      // Adjust chunk boundary to avoid splitting words
      const adjustedEnd = this.findChunkBoundary(text, chunkEnd);
      
      const chunk: DocumentChunk = {
        chunkId: `chunk_${chunkIndex}`,
        startPosition: position,
        endPosition: adjustedEnd,
        content: text.slice(position, adjustedEnd),
        isLoaded: false,
        isVisible: false,
        lastAccessed: new Date()
      };
      
      chunks.push(chunk);
      this.documentChunks.set(chunk.chunkId, chunk);
      
      position = adjustedEnd;
      chunkIndex++;
    }
    
    this.metrics.totalChunks = chunks.length;
    
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Created ${chunks.length} chunks (avg ${(this.config.chunkSize / 1024).toFixed(1)}KB each)`);
    
    return chunks;
  }

  /**
   * Find optimal chunk boundary (avoid splitting words/sentences)
   */
  private findChunkBoundary(text: string, idealEnd: number): number {
    if (idealEnd >= text.length) return text.length;
    
    // Look for paragraph break within 500 characters
    for (let i = idealEnd; i > idealEnd - 500 && i > 0; i--) {
      if (text[i] === '\n' && text[i + 1] === '\n') {
        return i + 2; // Include the paragraph break
      }
    }
    
    // Look for sentence break within 200 characters
    for (let i = idealEnd; i > idealEnd - 200 && i > 0; i--) {
      if (text[i] === '.' && text[i + 1] === ' ') {
        return i + 2; // Include the period and space
      }
    }
    
    // Look for word break within 50 characters
    for (let i = idealEnd; i > idealEnd - 50 && i > 0; i--) {
      if (text[i] === ' ') {
        return i + 1; // Include the space
      }
    }
    
    // Fallback to ideal end
    return idealEnd;
  }

  // ============================================================================
  // VIEWPORT OPTIMIZATION
  // ============================================================================

  /**
   * Initialize viewport-based rendering
   */
  private async initializeViewportRendering(_chunks: DocumentChunk[]): Promise<void> {
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Initializing viewport rendering...');
    
    // Calculate initial visible chunks
    const visibleChunks = this.calculateVisibleChunks(0, window.innerHeight);
    
    // Load visible chunks
    for (const chunkId of visibleChunks) {
      await this.loadChunk(chunkId);
    }
    
    // Preload adjacent chunks
    const preloadChunks = this.calculatePreloadChunks(visibleChunks);
    for (const chunkId of preloadChunks) {
      this.preloadChunk(chunkId);
    }
    
    // Setup viewport change monitoring
    this.setupViewportMonitoring();
  }

  /**
   * Calculate visible chunks based on viewport
   */
  private calculateVisibleChunks(scrollTop: number, viewportHeight: number): string[] {
    const visibleChunks: string[] = [];
    
    // Estimate chunks in viewport (simplified calculation)
    const estimatedChunkHeight = 1000; // pixels per chunk (will be refined)
    const startChunk = Math.floor(scrollTop / estimatedChunkHeight);
    const endChunk = Math.ceil((scrollTop + viewportHeight) / estimatedChunkHeight);
    
    for (let i = startChunk; i <= endChunk && i < this.metrics.totalChunks; i++) {
      const chunkId = `chunk_${i}`;
      if (this.documentChunks.has(chunkId)) {
        visibleChunks.push(chunkId);
      }
    }
    
    return visibleChunks;
  }

  /**
   * Calculate chunks to preload
   */
  private calculatePreloadChunks(visibleChunks: string[]): string[] {
    const preloadChunks: string[] = [];
    
    if (visibleChunks.length === 0) return preloadChunks;
    
    // Get chunk indices
    const visibleIndices = visibleChunks.map(id => parseInt(id.split('_')[1]));
    const minIndex = Math.min(...visibleIndices);
    const maxIndex = Math.max(...visibleIndices);
    
    // Preload chunks before and after visible range
    for (let i = minIndex - this.config.preloadPages; i <= maxIndex + this.config.preloadPages; i++) {
      if (i >= 0 && i < this.metrics.totalChunks) {
        const chunkId = `chunk_${i}`;
        if (!visibleChunks.includes(chunkId) && this.documentChunks.has(chunkId)) {
          preloadChunks.push(chunkId);
        }
      }
    }
    
    return preloadChunks;
  }

  /**
   * Setup viewport change monitoring
   */
  private setupViewportMonitoring(): void {
    let scrollTimeout: NodeJS.Timeout | null = null;
    
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        this.handleViewportChange();
      }, 16); // ~60fps
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Viewport monitoring active');
  }

  /**
   * Handle viewport changes
   */
  private async handleViewportChange(): Promise<void> {
    const scrollTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    
    // Calculate new visible chunks
    const newVisibleChunks = this.calculateVisibleChunks(scrollTop, viewportHeight);
    
    // Unload chunks that are no longer visible
    for (const chunkId of this.visibleChunks) {
      if (!newVisibleChunks.includes(chunkId)) {
        this.unloadChunk(chunkId);
      }
    }
    
    // Load newly visible chunks
    for (const chunkId of newVisibleChunks) {
      if (!this.visibleChunks.has(chunkId)) {
        await this.loadChunk(chunkId);
      }
    }
    
    // Update visible chunks set
    this.visibleChunks = new Set(newVisibleChunks);
    
    // Preload adjacent chunks
    const preloadChunks = this.calculatePreloadChunks(newVisibleChunks);
    for (const chunkId of preloadChunks) {
      if (!this.loadedChunks.has(chunkId)) {
        this.preloadChunk(chunkId);
      }
    }
    
    // Update metrics
    this.metrics.visibleChunks = this.visibleChunks.size;
    this.metrics.loadedChunks = this.loadedChunks.size;
  }

  // ============================================================================
  // CHUNK MANAGEMENT
  // ============================================================================

  /**
   * Load chunk into memory and prepare for rendering
   */
  private async loadChunk(chunkId: string): Promise<void> {
    const chunk = this.documentChunks.get(chunkId);
    if (!chunk || chunk.isLoaded) return;
    
    const startTime = performance.now();
    
    try {
      // Load chunk content
      chunk.isLoaded = true;
      chunk.lastAccessed = new Date();
      
      // Calculate layout for chunk
      if (this.config.enableBackgroundProcessing && this.layoutWorker) {
        // Background layout calculation
        this.calculateChunkLayoutInBackground(chunk);
      } else {
        // Immediate layout calculation
        await this.calculateChunkLayout(chunk);
      }
      
      this.loadedChunks.add(chunkId);
      
      const loadTime = performance.now() - startTime;
      this.metrics.chunkLoadTime = (this.metrics.chunkLoadTime * 0.9) + (loadTime * 0.1);
      
      if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Loaded chunk ${chunkId} (${loadTime.toFixed(1)}ms)`);
      
    } catch (error) {
      console.error(`[LargeDocumentOptimizer] Failed to load chunk ${chunkId}:`, error);
      chunk.isLoaded = false;
    }
  }

  /**
   * Preload chunk in background
   */
  private preloadChunk(chunkId: string): void {
    // Low priority background loading
    requestIdleCallback(() => {
      this.loadChunk(chunkId);
    });
  }

  /**
   * Unload chunk to free memory
   */
  private unloadChunk(chunkId: string): void {
    const chunk = this.documentChunks.get(chunkId);
    if (!chunk || !chunk.isLoaded) return;
    
    // Check if chunk should be kept (recently accessed)
    const timeSinceAccess = Date.now() - chunk.lastAccessed.getTime();
    if (timeSinceAccess < 30000) { // Keep for 30 seconds
      return;
    }
    
    // Unload chunk data
    chunk.renderData = undefined;
    chunk.isLoaded = false;
    chunk.isVisible = false;
    
    this.loadedChunks.delete(chunkId);
    this.visibleChunks.delete(chunkId);
    
    // Force garbage collection hint
    BrowserAPI.callGarbageCollection();
    
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Unloaded chunk ${chunkId}`);
  }

  // ============================================================================
  // LAYOUT CALCULATION
  // ============================================================================

  /**
   * Calculate layout for chunk
   */
  private async calculateChunkLayout(chunk: DocumentChunk): Promise<void> {
    const startTime = performance.now();
    
    try {
      // Parse text into lines
      const lines = this.parseChunkIntoLines(chunk.content);
      
      // Calculate glyph positions for each line
      const renderedLines: TextLine[] = [];
      let yPosition = 0;
      
      for (const line of lines) {
        const renderedLine = await this.calculateLineLayout(line, yPosition);
        renderedLines.push(renderedLine);
        yPosition += renderedLine.height;
      }
      
      // Create render data
      chunk.renderData = {
        lines: renderedLines,
        height: yPosition,
        width: Math.max(...renderedLines.map(l => this.calculateLineWidth(l))),
        glyphCache: new Map(),
        layoutComplete: true
      };
      
      const layoutTime = performance.now() - startTime;
      this.metrics.layoutCalculationTime = (this.metrics.layoutCalculationTime * 0.9) + (layoutTime * 0.1);
      
    } catch (error) {
      console.error(`[LargeDocumentOptimizer] Layout calculation failed for ${chunk.chunkId}:`, error);
    }
  }

  /**
   * Calculate layout in background worker
   */
  private calculateChunkLayoutInBackground(chunk: DocumentChunk): void {
    if (!this.layoutWorker) return;
    
    this.layoutWorker.postMessage({
      action: 'calculateLayout',
      chunkId: chunk.chunkId,
      content: chunk.content,
      config: this.config
    });
  }

  /**
   * Parse chunk content into lines
   */
  private parseChunkIntoLines(content: string): string[] {
    return content.split('\n');
  }

  /**
   * Calculate layout for individual line
   */
  private async calculateLineLayout(lineText: string, yPosition: number): Promise<TextLine> {
    const lineId = `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Calculate glyphs (simplified - will use actual font metrics)
    const glyphs: GlyphData[] = [];
    let xPosition = 0;
    
    for (let i = 0; i < lineText.length; i++) {
      const char = lineText[i];
      const glyphWidth = this.estimateCharacterWidth(char);
      
      glyphs.push({
        character: char,
        x: xPosition,
        y: yPosition,
        width: glyphWidth,
        height: 16, // Estimated line height
        fontFamily: 'Times New Roman',
        fontSize: 12,
        color: '#000000'
      });
      
      xPosition += glyphWidth;
    }
    
    return {
      lineId,
      text: lineText,
      yPosition,
      height: 20, // Line height with spacing
      glyphs,
      formatting: this.getDefaultLineFormatting()
    };
  }

  private estimateCharacterWidth(char: string): number {
    // Simplified character width estimation
    // Will be replaced with actual font metrics
    if (char === ' ') return 4;
    if (char === 'i' || char === 'l') return 3;
    if (char === 'w' || char === 'm') return 10;
    return 7; // Average character width
  }

  private calculateLineWidth(line: TextLine): number {
    return line.glyphs.reduce((width, glyph) => width + glyph.width, 0);
  }

  private getDefaultLineFormatting(): LineFormatting {
    return {
      fontFamily: 'Times New Roman',
      fontSize: 12,
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: '#000000',
      textAlign: 'left',
      lineHeight: 1.5
    };
  }

  // ============================================================================
  // BACKGROUND PROCESSING
  // ============================================================================

  private initializeBackgroundProcessing(): void {
    if (!this.config.enableBackgroundProcessing) return;
    
    try {
      // Initialize layout worker
      this.layoutWorker = new Worker('/workers/layout-worker.js');
      this.layoutWorker.onmessage = this.handleLayoutWorkerMessage.bind(this);
      
      // Initialize chunk processing worker
      this.chunkWorker = new Worker('/workers/chunk-worker.js');
      this.chunkWorker.onmessage = this.handleChunkWorkerMessage.bind(this);
      
      if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Background processing initialized');
      
    } catch (error) {
      console.warn('[LargeDocumentOptimizer] Background processing not available:', error);
      this.config.enableBackgroundProcessing = false;
    }
  }

  private startBackgroundProcessing(chunks: DocumentChunk[]): void {
    if (!this.layoutWorker) return;
    
    // Queue non-visible chunks for background processing
    for (const chunk of chunks) {
      if (!this.visibleChunks.has(chunk.chunkId)) {
        this.layoutWorker.postMessage({
          action: 'queueLayout',
          chunkId: chunk.chunkId,
          content: chunk.content,
          priority: 'low'
        });
      }
    }
    
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Queued ${chunks.length - this.visibleChunks.size} chunks for background processing`);
  }

  private handleLayoutWorkerMessage(event: MessageEvent): void {
    const { action, chunkId, result } = event.data;
    
    switch (action) {
      case 'layoutComplete':
        this.handleBackgroundLayoutComplete(chunkId, result);
        break;
        
      case 'layoutError':
        console.error(`[LargeDocumentOptimizer] Background layout failed for ${chunkId}:`, result.error);
        break;
    }
  }

  private handleChunkWorkerMessage(event: MessageEvent): void {
    const { action, chunkId, result } = event.data;
    
    switch (action) {
      case 'chunkProcessed':
        this.handleBackgroundChunkProcessed(chunkId, result);
        break;
        
      case 'chunkError':
        console.error(`[LargeDocumentOptimizer] Background chunk processing failed for ${chunkId}:`, result.error);
        break;
    }
  }

  private handleBackgroundLayoutComplete(chunkId: string, layoutResult: any): void {
    const chunk = this.documentChunks.get(chunkId);
    if (chunk) {
      chunk.renderData = layoutResult;
      if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Background layout complete for ${chunkId}`);
    }
  }

  private handleBackgroundChunkProcessed(chunkId: string, _processResult: any): void {
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Background processing complete for ${chunkId}`);
  }

  // ============================================================================
  // MEMORY OPTIMIZATION
  // ============================================================================

  /**
   * Optimize memory usage
   */
  async optimizeMemoryUsage(): Promise<{
    memoryFreed: number;
    chunksUnloaded: number;
  }> {
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Optimizing memory usage...');
    
    const initialMemory = this.memoryManager.getCurrentUsage();
    let chunksUnloaded = 0;
    
    // Get chunks sorted by last access time
    const chunks = Array.from(this.documentChunks.values())
      .filter(chunk => chunk.isLoaded && !this.visibleChunks.has(chunk.chunkId))
      .sort((a, b) => a.lastAccessed.getTime() - b.lastAccessed.getTime());
    
    // Unload oldest chunks if over memory threshold
    while (this.memoryManager.getCurrentUsage() > this.config.memoryThreshold && chunks.length > 0) {
      const oldestChunk = chunks.shift();
      if (oldestChunk) {
        this.unloadChunk(oldestChunk.chunkId);
        chunksUnloaded++;
      }
    }
    
    const finalMemory = this.memoryManager.getCurrentUsage();
    const memoryFreed = initialMemory - finalMemory;
    
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Memory optimization complete: ${memoryFreed.toFixed(1)}MB freed, ${chunksUnloaded} chunks unloaded`);
    
    return { memoryFreed, chunksUnloaded };
  }

  /**
   * Clear all chunks
   */
  private clearChunks(): void {
    this.documentChunks.clear();
    this.visibleChunks.clear();
    this.loadedChunks.clear();
    
    this.metrics.totalChunks = 0;
    this.metrics.loadedChunks = 0;
    this.metrics.visibleChunks = 0;
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Cleared all chunks');
  }

  // ============================================================================
  // PERFORMANCE MONITORING
  // ============================================================================

  /**
   * Update optimization metrics
   */
  private updateOptimizationMetrics(): void {
    this.metrics.memoryUsage = this.memoryManager.getCurrentUsage();
    this.metrics.frameRate = this.performanceMonitor.getCurrentFrameRate();
    this.metrics.renderTime = this.performanceMonitor.getAverageRenderTime();
    const now = Date.now();
    
    // Add to history
    this.performanceHistory.push({ ...this.metrics });
    
    // Trim history
    if (this.performanceHistory.length > 100) {
      this.performanceHistory = this.performanceHistory.slice(-50);
    }
    
    // Log performance warnings
    if (this.metrics.frameRate < this.config.performanceTarget * 0.8) {
      if (now - this.lastLowFpsWarnAt > 60000) {
        console.warn(`[LargeDocumentOptimizer] Low frame rate: ${this.metrics.frameRate.toFixed(1)}fps`);
        this.lastLowFpsWarnAt = now;
      }
    }
    
    // Determine adaptive threshold from memory manager (it adapts to jsHeapSizeLimit)
    const adaptiveThreshold = (this as any).memoryManager ? (this as any).memoryManager['memoryThreshold'] : this.config.memoryThreshold;
    if (this.metrics.memoryUsage > adaptiveThreshold * 0.95) {
      const delta = Math.abs(this.metrics.memoryUsage - this.prevMemoryUsage);
      if (delta >= 8 || (now - this.lastHighMemWarnAt > 60000)) {
        console.warn(`[LargeDocumentOptimizer] High memory usage: ${this.metrics.memoryUsage.toFixed(1)}MB (threshold ~${adaptiveThreshold}MB)`);
        this.lastHighMemWarnAt = now;
      }
    }
    this.prevMemoryUsage = this.metrics.memoryUsage;
  }

  /**
   * Monitor performance continuously
   */
  startPerformanceMonitoring(): void {
    setInterval(() => {
      this.updateOptimizationMetrics();
      
      // Auto-optimize if performance degrades
      if (this.shouldAutoOptimize()) {
        this.performAutoOptimization();
      }
    }, 5000); // Every 5 seconds
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Performance monitoring started');
  }

  private shouldAutoOptimize(): boolean {
    return this.metrics.frameRate < this.config.performanceTarget * 0.7 ||
           this.metrics.memoryUsage > this.config.memoryThreshold * 0.9 ||
           this.metrics.renderTime > 50; // 50ms render time threshold
  }

  private async performAutoOptimization(): Promise<void> {
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Performing auto-optimization...');
    
    // Memory optimization
    if (this.metrics.memoryUsage > this.config.memoryThreshold * 0.9) {
      await this.optimizeMemoryUsage();
    }
    
    // Reduce quality if frame rate is low
    if (this.metrics.frameRate < this.config.performanceTarget * 0.7) {
      this.reduceRenderingQuality();
    }
    
    // Increase chunk size if render time is high
    if (this.metrics.renderTime > 50) {
      this.increaseChunkSize();
    }
  }

  private reduceRenderingQuality(): void {
    // Temporarily reduce rendering quality for performance
    this.viewportRenderer.setQualityMode('performance');
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Reduced rendering quality for performance');
  }

  private increaseChunkSize(): void {
    // Increase chunk size to reduce overhead
    this.config.chunkSize = Math.min(this.config.chunkSize * 1.5, 50000);
    
    if (this.debugEnabled) console.log(`[LargeDocumentOptimizer] Increased chunk size to ${this.config.chunkSize}`);
  }

  // ============================================================================
  // OPERATION OPTIMIZATION
  // ============================================================================

  /**
   * Optimize operations for large documents
   */
  async optimizeOperation(operation: Operation, _documentState: LocalDocumentState): Promise<{
    optimizedOperation: Operation;
    affectedChunks: string[];
    optimizationApplied: string[];
  }> {
    const optimizationApplied: string[] = [];
    
    // Find affected chunks
    const affectedChunks = this.findAffectedChunks(operation);
    
    // Optimize operation based on type
    let optimizedOperation = operation;
    
    switch (operation.type) {
      case 'insert':
        if (OperationTypeGuards.isInsertOperation(operation)) {
          optimizedOperation = await this.optimizeInsertOperation(operation);
        }
        optimizationApplied.push('batch_insertion');
        break;
        
      case 'delete':
        if (OperationTypeGuards.isDeleteOperation(operation)) {
          optimizedOperation = await this.optimizeDeleteOperation(operation);
        }
        optimizationApplied.push('range_deletion');
        break;
        
      default:
        // No optimization for other operations
        break;
    }
    
    return {
      optimizedOperation,
      affectedChunks,
      optimizationApplied
    };
  }

  private findAffectedChunks(operation: Operation): string[] {
    const affectedChunks: string[] = [];
    
    // Simplified - find chunks that contain the operation position
    for (const [chunkId, chunk] of this.documentChunks) {
      const operationPos = OperationTypeGuards.hasPosition(operation) ? operation.position : 0;
      
      if (operationPos >= chunk.startPosition && operationPos <= chunk.endPosition) {
        affectedChunks.push(chunkId);
      }
    }
    
    return affectedChunks;
  }

  private async optimizeInsertOperation(operation: TypedInsertOperation): Promise<Operation> {
    // Batch multiple character insertions
    if (this.optimizationQueue.length > 0) {
      const lastOp = this.optimizationQueue[this.optimizationQueue.length - 1];
      
      if (lastOp.type === 'insert' && 
          Math.abs(lastOp.position - operation.position) <= 1) {
        // Combine with previous insertion
        return {
          ...lastOp,
          content: lastOp.content + operation.content,
          position: Math.min(lastOp.position, operation.position)
        };
      }
    }
    
    return operation;
  }

  private async optimizeDeleteOperation(operation: TypedDeleteOperation): Promise<Operation> {
    // Optimize range deletions
    return operation;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get optimization metrics
   */
  getOptimizationMetrics(): OptimizationMetrics {
    return { ...this.metrics };
  }

  /**
   * Get performance history
   */
  getPerformanceHistory(): OptimizationMetrics[] {
    return [...this.performanceHistory];
  }

  /**
   * Get chunk information
   */
  getChunkInfo(): {
    totalChunks: number;
    loadedChunks: number;
    visibleChunks: number;
    memoryUsage: number;
  } {
    return {
      totalChunks: this.documentChunks.size,
      loadedChunks: this.loadedChunks.size,
      visibleChunks: this.visibleChunks.size,
      memoryUsage: this.metrics.memoryUsage
    };
  }

  /**
   * Force optimization
   */
  async forceOptimization(documentState: LocalDocumentState): Promise<void> {
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Force optimization requested');
    await this.optimizeDocument(documentState);
  }

  /**
   * Update configuration
   */
  updateConfiguration(newConfig: Partial<OptimizationConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Apply configuration changes
    this.viewportRenderer.updateConfiguration(this.config);
    this.memoryManager.updateThreshold(this.config.memoryThreshold);
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Configuration updated:', newConfig);
  }

  /**
   * Check if document requires optimization
   */
  shouldOptimizeDocument(documentState: LocalDocumentState): boolean {
    const textLength = documentState.text.length;
    const estimatedPages = textLength / 2000; // ~2000 chars per page
    
    return estimatedPages > 10 || // More than 10 pages
           textLength > 100000 ||  // More than 100KB
           this.metrics.memoryUsage > this.config.memoryThreshold * 0.8;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy optimizer and free resources
   */
  async destroy(): Promise<void> {
    // Stop background processing
    if (this.layoutWorker) {
      this.layoutWorker.terminate();
    }
    
    if (this.chunkWorker) {
      this.chunkWorker.terminate();
    }
    
    // Clear all chunks
    this.clearChunks();
    
    // Destroy components
    this.viewportRenderer.destroy();
    this.memoryManager.destroy();
    this.performanceMonitor.destroy();
    
    if (this.debugEnabled) console.log('[LargeDocumentOptimizer] Destroyed');
  }
}
